import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Rating from "@mui/material/Rating";
import Slider from "@mui/material/Slider";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import { postRecord } from "services/services";
import { AuthContext } from "context/Authentication";
import Header from "../../components/Header/Header.jsx";
import Footer from "../../components/Footer/Footer.jsx";
import ProductCard from "CommonComponents/ProductCard/ProductCard.jsx";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChevronRight from "@mui/icons-material/ChevronRight";
import FilterListIcon from "@mui/icons-material/FilterList";

const API_GetAllCategories = "productcategories/getall";
const API_GetSubcategoriesByCategoryId = "productsubcategories/getbycategoryid";
const API_GetAllProductsWithFilters = "products/getallwithfilters";

export default function AllProducts() {
  const { category, subcategory, tags } = useParams();
  const navigate = useNavigate();
  const context = useContext(AuthContext);

  // iOS Safari detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari =
    /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [openCategory, setOpenCategory] = useState(null);
  const [headerRefresh, setHeaderRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nearBottom, setNearBottom] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [displayedRecords, setDisplayedRecords] = useState(0);

  // Filter states
  const [isFeatured, setIsFeatured] = useState(false);
  const [isPopular, setIsPopular] = useState(false);
  const [isBestSeller, setIsBestSeller] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false); // Add state for mobile filter panel

  // Ref for the main product container
  const productContainerRef = useRef(null);

  // Helper function to build tag names for API
  const buildTagNames = () => {
    const tagNames = [];
    if (tags) tagNames.push(tags);
    if (isPopular) tagNames.push("Popular");
    if (isBestSeller) tagNames.push("Best Seller");
    return tagNames.join(",");
  };

  // Helper function to apply rating filter
  const applyRatingFilter = (products) => {
    if (ratingFilter === 0) return products;
    return products.filter((product) => product.averageRating >= ratingFilter);
  };

  // Scroll handler for infinite scroll - iOS Safari compatible
  useEffect(() => {
    let scrollTimer = null;

    const handleScroll = () => {
      if (!hasMore || loadingMore) return;

      // Clear existing timer
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }

      // Debounce the scroll handling to prevent rapid API calls
      scrollTimer = setTimeout(
        () => {
          // iOS Safari compatible scroll position detection
          const scrollTop = Math.max(
            window.pageYOffset || 0,
            document.documentElement.scrollTop || 0,
            document.body.scrollTop || 0
          );

          // iOS Safari compatible height calculations
          const scrollHeight = Math.max(
            document.body.scrollHeight || 0,
            document.body.offsetHeight || 0,
            document.documentElement.clientHeight || 0,
            document.documentElement.scrollHeight || 0,
            document.documentElement.offsetHeight || 0
          );

          // iOS Safari compatible viewport height
          const clientHeight = Math.min(
            document.documentElement.clientHeight || window.innerHeight,
            window.innerHeight || document.documentElement.clientHeight
          );

          console.log(
            `iOS Safari scroll debug - scrollTop: ${scrollTop}, scrollHeight: ${scrollHeight}, clientHeight: ${clientHeight}, isIOSSafari: ${isIOSSafari}`
          );

          // iOS Safari needs larger thresholds due to momentum scrolling and UI behavior
          const bottomThreshold = isIOSSafari ? 800 : 500;
          const nearBottomThreshold = isIOSSafari ? 1000 : 600;
          const debounceDelay = isIOSSafari ? 50 : 100; // Faster response on iOS

          // Calculate how close we are to the bottom (increased threshold for iOS Safari)
          const scrolledToBottom =
            scrollTop + clientHeight >= scrollHeight - bottomThreshold;

          // Set nearBottom state for visual feedback (increased for iOS)
          setNearBottom(
            scrollTop + clientHeight >= scrollHeight - nearBottomThreshold
          );

          // Trigger loading when scrolling to within threshold of the document bottom
          if (scrolledToBottom && !loadingMore) {
            console.log(
              `${isIOSSafari ? "iOS Safari" : "Desktop"} - Triggering load more`
            );
            setPageIndex((prevPage) => prevPage + 1);
          }
        },
        isIOSSafari ? 50 : 100
      ); // Faster debounce for iOS responsiveness
    };

    // iOS Safari compatible event handling
    const throttledHandleScroll = () => {
      // Use requestAnimationFrame for iOS Safari compatibility
      if (typeof requestAnimationFrame !== "undefined") {
        requestAnimationFrame(handleScroll);
      } else {
        handleScroll();
      }
    };

    // Add multiple event listeners for iOS Safari compatibility
    window.addEventListener("scroll", throttledHandleScroll, { passive: true });
    window.addEventListener("touchmove", throttledHandleScroll, {
      passive: true,
    });
    document.addEventListener("scroll", throttledHandleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      window.removeEventListener("touchmove", throttledHandleScroll);
      document.removeEventListener("scroll", throttledHandleScroll);
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [loadingMore, hasMore, isIOSSafari]);

  // iOS Safari fallback using Intersection Observer (more reliable on iOS)
  useEffect(() => {
    if (!isIOSSafari || !("IntersectionObserver" in window)) return;

    const sentinel = document.createElement("div");
    sentinel.style.height = "1px";
    sentinel.style.position = "absolute";
    sentinel.style.bottom = "1000px";
    sentinel.setAttribute("data-sentinel", "true");

    document.body.appendChild(sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore && !loadingMore) {
            console.log(
              "iOS Safari - Intersection Observer triggered load more"
            );
            setPageIndex((prevPage) => prevPage + 1);
          }
        });
      },
      {
        rootMargin: "0px 0px 800px 0px", // Trigger 800px before reaching the sentinel
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (document.body.contains(sentinel)) {
        document.body.removeChild(sentinel);
      }
    };
  }, [isIOSSafari, hasMore, loadingMore]);

  useEffect(() => {
    let isMounted = true;

    // Fetch all categories
    const fetchCategories = async () => {
      try {
        const response = await postRecord(API_GetAllCategories, {});
        if (response.status === 200 && response.data && isMounted) {
          setCategories(
            response.data.filter(
              (category) =>
                category.Category_Type === "Product" &&
                (!category.Category_Tags ||
                  !category.Category_Tags.includes("School Menu"))
            )
          );
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Reset pagination when route params or filters change
    setPageIndex(1);
    setProducts([]);
    setHasMore(true);
    setTotalRecords(0);
    setDisplayedRecords(0);
    setNearBottom(false);
  }, [
    category,
    subcategory,
    tags,
    isFeatured,
    isPopular,
    isBestSeller,
    recordsPerPage,
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async (page = 1, append = false) => {
      try {
        if (page === 1 && !append) {
          setLoading(true);
          setNearBottom(false);
        } else {
          setLoadingMore(true);
          setNearBottom(false);
        }

        console.log(
          `Fetching products - Page: ${page}, Append: ${append}, LoadingMore: ${
            page > 1
          }`
        );

        const objData = {
          Product_Category_Id: 0,
          Product_Sub_Category_Id: 0,
          Category_Name: category ? category.replace(/-/g, " ") : "",
          Sub_Category_Name: subcategory ? subcategory.replace(/-/g, " ") : "",
          Tag_Name: buildTagNames(),
          Sort_Column: "Modified_On",
          Sort_Type: "desc",
          Page_Index: page,
          Records_Per_Page: recordsPerPage,
          Is_Published: "1",
          Is_Deleted: "0",
          Is_Featured: isFeatured ? "1" : "0",
          On_Homepage: "0",
          On_Mainmenu: "0",
          Is_Addon: "0",
        };
        // console.log("API_GetAllProductsWithFilters request data:", objData);
        const response = await postRecord(
          API_GetAllProductsWithFilters,
          objData
        );
        // console.log("API_GetAllProductsWithFilters response:", response);
        if (isMounted && response.status === 200 && response.data) {
          // Transform the data to match the expected format
          console.log(
            `API Response - Products received: ${
              response.data.products?.length || 0
            }, Total records: ${response.data.total_records || 0}`
          );

          const transformedProducts = response.data.products
            .filter((x) => x.Category_Name !== "School Menu")
            .map((product) => ({
              ...product,
              image:
                product.default_media && product.default_media.length > 0
                  ? product.default_media[0]
                  : null,
              price: product.default_inventory
                ? product.default_inventory.Total_Selling_Price
                : 0,
              combinationText: product.default_combination
                ? product.default_combination.Combination
                : "",
              averageRating: product.average_review || 0,
              reviewCount: 0, // This will be handled by individual components if needed
              Default_Media: product.default_media || [],
              Default_Inventory: product.default_inventory,
              Default_Combination: product.default_combination,
            }));

          console.log(`Transformed products: ${transformedProducts.length}`);

          if (append) {
            setProducts((prevProducts) => {
              console.log(
                `Appending ${transformedProducts.length} products to existing ${prevProducts.length}`
              );
              return [...prevProducts, ...transformedProducts];
            });
            setDisplayedRecords((prev) => prev + transformedProducts.length);
          } else {
            setProducts(transformedProducts);
            setDisplayedRecords(transformedProducts.length);
            setTotalRecords(
              response.data.total_records || transformedProducts.length
            );

            // For first page load, clear loading immediately
            setLoading(false);
          }

          // Check if there are more records - improved logic
          const hasMoreRecords = transformedProducts.length === recordsPerPage;
          console.log(
            `Has more records: ${hasMoreRecords} (received: ${transformedProducts.length}, per page: ${recordsPerPage})`
          );
          setHasMore(hasMoreRecords);

          // If no more products, ensure we clear the nearBottom state
          if (!hasMoreRecords) {
            setNearBottom(false);
          }
        } else {
          console.log("No valid response or no products received");
          // If no valid response, assume no more products
          if (append) {
            setHasMore(false);
            setNearBottom(false);
            setLoadingMore(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching products:", error);
          // On error, stop loading and assume no more products for this session
          if (append) {
            setHasMore(false);
            setNearBottom(false);
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      } finally {
        if (isMounted) {
          console.log(
            `Finishing API call - Only clearing loading state for first page`
          );
          // Only clear loading state for first page loads if not already cleared
          // loadingMore is handled in the success block with proper timing
          if (pageIndex === 1) {
            setLoading(false);
          }
          // For append operations (pageIndex > 1), loadingMore is cleared in the success block or error block
        }
      }
    };

    if (pageIndex === 1) {
      fetchProducts(1, false);
    } else {
      fetchProducts(pageIndex, true);
    }

    return () => {
      isMounted = false;
    };
  }, [
    pageIndex,
    category,
    subcategory,
    tags,
    isFeatured,
    isPopular,
    isBestSeller,
    recordsPerPage,
  ]);

  // Apply rating filter to products
  useEffect(() => {
    const filtered = applyRatingFilter(products);
    setFilteredProducts(filtered);
  }, [products, ratingFilter]);

  // Clear loading state after products have been updated (this ensures UI has rendered)
  useEffect(() => {
    if (products.length > 0 && loadingMore && pageIndex > 1) {
      // Add a small delay to ensure DOM has updated with new products
      const timeoutId = setTimeout(() => {
        console.log("Clearing loadingMore state after products rendered");
        setLoadingMore(false);
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [products.length, loadingMore, pageIndex]);

  // Function to load more products
  const loadMoreProducts = () => {
    if (!loadingMore && hasMore) {
      setPageIndex((prevPage) => prevPage + 1);
    }
  };

  // Breadcrumb navigation functions
  const handleBreadcrumbClick = (type, value) => {
    switch (type) {
      case "home":
        navigate("/");
        break;
      case "category":
        const formattedCategory = value.toLowerCase().replace(/\s+/g, "-");
        navigate(`/ProductList/${formattedCategory}`);
        break;
      case "subcategory":
        const formattedCat = category.toLowerCase().replace(/\s+/g, "-");
        const formattedSubcat = value.toLowerCase().replace(/\s+/g, "-");
        navigate(`/ProductList/${formattedCat}/${formattedSubcat}`);
        break;
      case "tag":
        const formattedTag = value.toLowerCase().replace(/\s+/g, "-");
        navigate(`/ProductList/NA/NA/${formattedTag}`);
        break;
      case "clearFeatured":
        setIsFeatured(false);
        break;
      case "clearPopular":
        setIsPopular(false);
        break;
      case "clearBestSeller":
        setIsBestSeller(false);
        break;
      case "clearRating":
        setRatingFilter(0);
        break;
      default:
        break;
    }
  };

  // Get default tags from app settings
  const defaultTags =
    context.state?.appSettings
      ?.find(
        (setting) =>
          setting.Setting_Name === "Default Tags" && setting.Is_active === "1"
      )
      ?.Setting_Value?.split(",")
      .filter((tag) => tag.trim() !== "") || [];

  const handleCategoryClick = (category) => {
    const formattedCategoryName = category.Category_Name.toLowerCase().replace(
      /\s+/g,
      "-"
    );
    setOpenCategory(
      openCategory === category.Category_Id ? null : category.Category_Id
    );

    // Fetch subcategories for the selected category
    postRecord(API_GetSubcategoriesByCategoryId, {
      Category_Id: category.Category_Id,
    }).then((response) => {
      if (response.status === 200 && response.data) {
        setSubcategories(response.data);
      }
    });

    navigate(`/ProductList/${formattedCategoryName}`);
  };

  const handleSubcategoryClick = (categoryName, subcategoryName) => {
    const formattedCategoryName = categoryName
      .toLowerCase()
      .replace(/\s+/g, "-");
    const formattedSubCategoryName = subcategoryName
      .toLowerCase()
      .replace(/\s+/g, "-");
    navigate(
      `/ProductList/${formattedCategoryName}/${formattedSubCategoryName}`
    );
  };

  const handleTagClick = (tag) => {
    const formattedTag = tag.toLowerCase().replace(/\s+/g, "-");
    navigate(`/ProductList/NA/NA/${formattedTag}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // iOS Safari scroll fixes
        WebkitOverflowScrolling: "touch", // Enable momentum scrolling on iOS
        overflowY: "auto", // Ensure scrolling is enabled
        position: "relative", // Fix positioning context
      }}
    >
      <Header
        headerRefresh={headerRefresh}
        setHeaderRefresh={setHeaderRefresh}
      />
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 4 },
          minHeight: "100vh",
          // iOS Safari specific fixes
          minHeight: [
            "100vh", // Standard browsers
            "100vh", // iOS Safari fallback
            "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))", // iOS Safari with safe areas
          ],
          pt: { xs: 10, md: 12 }, // Add top padding for header
        }}
      >
        {/* Breadcrumb Navigation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            p: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(0, 0, 0, 0.7)"
                : "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(15px)",
            borderRadius: 2,
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0, 0, 0, 0.6)"
                : "0 4px 20px rgba(0, 0, 0, 0.1)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Button
              onClick={() => handleBreadcrumbClick("home")}
              startIcon={<HomeOutlinedIcon />}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              Home
            </Button>

            {category && category !== "NA" && (
              <>
                <ChevronRight sx={{ color: "text.disabled", mx: 1 }} />
                <Button
                  onClick={() =>
                    handleBreadcrumbClick(
                      "category",
                      category.replace(/-/g, " ")
                    )
                  }
                  sx={{
                    color: "text.secondary",
                    textTransform: "capitalize",
                    fontWeight: 500,
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {category.replace(/-/g, " ")}
                </Button>
              </>
            )}

            {subcategory && subcategory !== "NA" && (
              <>
                <ChevronRight sx={{ color: "text.disabled", mx: 1 }} />
                <Button
                  onClick={() =>
                    handleBreadcrumbClick(
                      "subcategory",
                      subcategory.replace(/-/g, " ")
                    )
                  }
                  sx={{
                    color: "text.secondary",
                    textTransform: "capitalize",
                    fontWeight: 500,
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {subcategory.replace(/-/g, " ")}
                </Button>
              </>
            )}

            {tags && (
              <>
                <ChevronRight sx={{ color: "text.disabled", mx: 1 }} />
                <Button
                  onClick={() =>
                    handleBreadcrumbClick("tag", tags.replace(/-/g, " "))
                  }
                  sx={{
                    color: "text.secondary",
                    textTransform: "capitalize",
                    fontWeight: 500,
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {tags.replace(/-/g, " ")}
                </Button>
              </>
            )}

            {/* Filter chips */}
            {(isFeatured || isPopular || isBestSeller || ratingFilter > 0) && (
              <Box sx={{ ml: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {isFeatured && (
                  <Chip
                    label="Featured"
                    onDelete={() => handleBreadcrumbClick("clearFeatured")}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                {isPopular && (
                  <Chip
                    label="Popular"
                    onDelete={() => handleBreadcrumbClick("clearPopular")}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                {isBestSeller && (
                  <Chip
                    label="Best Seller"
                    onDelete={() => handleBreadcrumbClick("clearBestSeller")}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                {ratingFilter > 0 && (
                  <Chip
                    label={`${ratingFilter}+ Stars`}
                    onDelete={() => handleBreadcrumbClick("clearRating")}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Showing count on the right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: loadingMore ? 0.8 : 1,
              transition: "opacity 0.3s ease",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                mr: 1,
              }}
            >
              Showing
            </Typography>
            <Chip
              label={displayedRecords}
              color="primary"
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </Box>

        {/* Mobile Filter Toggle Button */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            mb: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.6)"
                  : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.8)"
                    : "rgba(255, 255, 255, 1)",
              },
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              display: { xs: showFilters ? "block" : "none", md: "block" },
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: 100 },
                zIndex: 10,
                transition: "all 0.3s ease",
                overflow: "hidden",
                animation: {
                  xs: showFilters ? "slideDown 0.3s ease-out" : "none",
                  md: "none",
                },
                "@keyframes slideDown": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-20px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
                "&:hover": {
                  transform: { xs: "none", md: "translateY(-2px)" },
                  boxShadow: { xs: 2, md: 4 },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <FilterListIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    letterSpacing: 0.5,
                  }}
                >
                  Filters
                </Typography>
              </Box>

              {/* Records Per Page */}
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={recordsPerPage}
                  label="Items per page"
                  onChange={(e) => setRecordsPerPage(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.1)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              {/* Toggle Filters */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
                >
                  Quick Filters
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isFeatured}
                      onChange={(e) => setIsFeatured(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Featured Products"
                  sx={{
                    width: "100%",
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={isPopular}
                      onChange={(e) => setIsPopular(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Popular Items"
                  sx={{
                    width: "100%",
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={isBestSeller}
                      onChange={(e) => setIsBestSeller(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Best Sellers"
                  sx={{
                    width: "100%",
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Rating Filter */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
                >
                  Minimum Rating
                </Typography>
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={ratingFilter}
                    onChange={(e, newValue) => setRatingFilter(newValue)}
                    min={0}
                    max={5}
                    step={1}
                    marks={[
                      { value: 0, label: "All" },
                      { value: 1, label: "1★" },
                      { value: 2, label: "2★" },
                      { value: 3, label: "3★" },
                      { value: 4, label: "4★" },
                      { value: 5, label: "5★" },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) =>
                      value === 0 ? "All" : `${value}★+`
                    }
                    sx={{
                      "& .MuiSlider-mark": {
                        display: "none",
                      },
                      "& .MuiSlider-markLabel": {
                        fontSize: "0.7rem",
                        transform: "translateX(-50%) translateY(8px)",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Categories */}
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Categories
              </Typography>
              <List
                sx={{
                  mb: 2,
                  maxHeight: 300,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {categories.map((category) => {
                  // Get image from Category_Media_Files (JSON column)
                  let categoryImage = null;
                  if (category.Category_Media_Files) {
                    try {
                      const mediaArr =
                        typeof category.Category_Media_Files === "string"
                          ? JSON.parse(category.Category_Media_Files)
                          : category.Category_Media_Files;
                      if (
                        Array.isArray(mediaArr) &&
                        mediaArr.length > 0 &&
                        typeof mediaArr[0] === "string"
                      ) {
                        categoryImage = mediaArr[0].startsWith("http")
                          ? mediaArr[0]
                          : `${process.env.BASEURL}${mediaArr[0]}`;
                      }
                    } catch (e) {}
                  }
                  const isOpen = openCategory === category.Category_Id;
                  return (
                    <React.Fragment key={category.Category_Id}>
                      <ListItemButton
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          background: isOpen
                            ? "linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))"
                            : "transparent",
                          border: isOpen
                            ? "1px solid"
                            : "1px solid transparent",
                          borderColor: isOpen ? "primary.main" : "transparent",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          minHeight: 48,
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.03))",
                            borderColor: "primary.main",
                            transform: "none", // Remove transform to prevent overflow
                          },
                        }}
                      >
                        {categoryImage && (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 2,
                              overflow: "hidden",
                              marginRight: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              transition: "all 0.3s ease",
                            }}
                          >
                            <img
                              src={categoryImage}
                              alt={category.Category_Name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              loading="lazy"
                            />
                          </Box>
                        )}
                        <ListItemText
                          primary={category.Category_Name}
                          primaryTypographyProps={{
                            fontWeight: isOpen ? 600 : 500,
                            fontSize: 14,
                            color: isOpen ? "primary.main" : "text.primary",
                          }}
                          sx={{ mr: 1 }}
                        />
                        <Box sx={{ ml: "auto" }}>
                          {isOpen ? (
                            <ExpandLessIcon
                              sx={{ fontSize: 18, color: "primary.main" }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            />
                          )}
                        </Box>
                      </ListItemButton>
                      <Collapse in={isOpen} timeout={300}>
                        <List component="div" disablePadding sx={{ ml: 2 }}>
                          {subcategories
                            .filter(
                              (sub) => sub.Category_Id === category.Category_Id
                            )
                            .map((subcategory) => {
                              // Get image from SubCategory_Media_Files (JSON column)
                              let subcatImage = null;
                              if (subcategory.SubCategory_Media_Files) {
                                try {
                                  const subMediaArr =
                                    typeof subcategory.SubCategory_Media_Files ===
                                    "string"
                                      ? JSON.parse(
                                          subcategory.SubCategory_Media_Files
                                        )
                                      : subcategory.SubCategory_Media_Files;
                                  if (
                                    Array.isArray(subMediaArr) &&
                                    subMediaArr.length > 0 &&
                                    typeof subMediaArr[0] === "string"
                                  ) {
                                    subcatImage = subMediaArr[0].startsWith(
                                      "http"
                                    )
                                      ? subMediaArr[0]
                                      : `${process.env.BASEURL}${subMediaArr[0]}`;
                                  }
                                } catch (e) {}
                              }
                              return (
                                <ListItemButton
                                  onClick={() =>
                                    handleSubcategoryClick(
                                      category.Category_Name,
                                      subcategory.Sub_Category_Name
                                    )
                                  }
                                  key={subcategory.Sub_Category_Id}
                                  sx={{
                                    borderRadius: 2,
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    background: "transparent",
                                    transition: "all 0.2s ease",
                                    minHeight: 36,
                                    p: 1,
                                    "&:hover": {
                                      background: "rgba(25, 118, 210, 0.05)",
                                      transform: "none", // Remove transform to prevent overflow
                                    },
                                  }}
                                >
                                  {subcatImage && (
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 1.5,
                                        overflow: "hidden",
                                        marginRight: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                      }}
                                    >
                                      <img
                                        src={subcatImage}
                                        alt={subcategory.Sub_Category_Name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                        loading="lazy"
                                      />
                                    </Box>
                                  )}
                                  <ListItemText
                                    primary={subcategory.Sub_Category_Name}
                                    primaryTypographyProps={{
                                      fontWeight: 500,
                                      fontSize: 13,
                                      color: "text.primary",
                                    }}
                                  />
                                </ListItemButton>
                              );
                            })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </List>

              {/* Add default tags as filters */}
              {defaultTags.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Quick Tags
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {defaultTags.slice(0, 6).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onClick={() => handleTagClick(tag)}
                        variant={
                          tags === tag.toLowerCase().replace(/\s+/g, "-")
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          tags === tag.toLowerCase().replace(/\s+/g, "-")
                            ? "primary"
                            : "default"
                        }
                        size="small"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          borderRadius: 2,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: 2,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            md={9}
            ref={productContainerRef}
            sx={{
              width: { xs: showFilters ? "100%" : "100%", md: "75%" },
            }}
          >
            <Grid container spacing={2}>
              {loading && pageIndex === 1 ? (
                // Skeleton loading for first page
                Array.from(new Array(12)).map((_, index) => (
                  <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: "background.paper",
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          mb: 2,
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "action.hover",
                            animation: "shimmer 1.5s infinite",
                            "@keyframes shimmer": {
                              "0%": { opacity: 0.5 },
                              "50%": { opacity: 1 },
                              "100%": { opacity: 0.5 },
                            },
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          height: 20,
                          mb: 1,
                          borderRadius: 1,
                          backgroundColor: "action.hover",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                      <Box
                        sx={{
                          height: 16,
                          mb: 2,
                          width: "70%",
                          borderRadius: 1,
                          backgroundColor: "action.hover",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                      <Box
                        sx={{
                          height: 18,
                          width: "50%",
                          borderRadius: 1,
                          backgroundColor: "action.hover",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                    </Paper>
                  </Grid>
                ))
              ) : filteredProducts.length > 0 ? (
                <>
                  {filteredProducts.map((product, index) => (
                    <Grid
                      item
                      xs={6}
                      sm={6}
                      md={4}
                      lg={3}
                      key={product.Product_Id}
                      sx={{
                        animation: `fadeInUp 0.6s ease forwards`,
                        animationDelay: `${(index % 12) * 0.1}s`,
                        opacity: 0,
                        "@keyframes fadeInUp": {
                          from: {
                            opacity: 0,
                            transform: "translateY(30px)",
                          },
                          to: {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        },
                      }}
                    >
                      <ProductCard
                        product={product}
                        loading={loading}
                        headerRefresh={headerRefresh}
                        setHeaderRefresh={setHeaderRefresh}
                      />
                    </Grid>
                  ))}

                  {/* Near bottom indicator - only show when not loading and has more products */}
                  {nearBottom && !loadingMore && hasMore && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 2,
                          opacity: 0.6,
                          animation: "pulse 1.5s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%": {
                              opacity: 0.4,
                            },
                            "50%": {
                              opacity: 0.8,
                            },
                            "100%": {
                              opacity: 0.4,
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          {isIOSSafari
                            ? "Scroll down to load more products... (iOS optimized)"
                            : "Scroll a little more to load more products..."}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Loading more indicator - stays visible until loading completes */}
                  {loadingMore && hasMore && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 6,
                          animation: "fadeInUp 0.3s ease-out",
                          "@keyframes fadeInUp": {
                            from: {
                              opacity: 0,
                              transform: "translateY(20px)",
                            },
                            to: {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <CircularProgress
                            size={40}
                            thickness={3}
                            sx={{
                              color: "primary.main",
                              animation: "spin 1s linear infinite",
                              "@keyframes spin": {
                                "0%": {
                                  transform: "rotate(0deg)",
                                },
                                "100%": {
                                  transform: "rotate(360deg)",
                                },
                              },
                            }}
                          />
                          <CircularProgress
                            size={40}
                            thickness={3}
                            variant="determinate"
                            value={25}
                            sx={{
                              color: "primary.light",
                              position: "absolute",
                              animation: "spin 2s linear infinite reverse",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            textAlign: "center",
                            mb: 1,
                          }}
                        >
                          Loading more products...
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            textAlign: "center",
                            maxWidth: 200,
                          }}
                        >
                          Finding the best deals for you
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Load More Button - Fallback for infinite scroll */}
                  {!loadingMore && hasMore && !nearBottom && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 4,
                        }}
                      >
                        <Button
                          variant="contained"
                          size="large"
                          onClick={loadMoreProducts}
                          sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: 2,
                            "&:hover": {
                              boxShadow: 4,
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {isIOSSafari
                            ? "Load More (Tap here if scroll isn't working)"
                            : "Load More Products"}
                        </Button>
                      </Box>
                    </Grid>
                  )}

                  {/* No more products message */}
                  {!hasMore && !loading && products.length > 0 && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          py: 4,
                          opacity: 0.7,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          🎉 You've seen all available products!
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      textAlign: "center",
                      backgroundColor: "background.paper",
                      borderRadius: 3,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <SentimentDissatisfiedIcon
                      sx={{
                        fontSize: 64,
                        color: "text.disabled",
                        mb: 2,
                        opacity: 0.7,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      No products found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.disabled",
                      }}
                    >
                      Try adjusting your filters or search criteria
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
}
